/**
 * The instance's module chain (API_ENDPOINTS.INSTANCE_CHAIN) and the module
 * catalog (API_ENDPOINTS.WORKSHOP_CATALOG) for /admin/workshop (#683).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import API_ENDPOINTS from '../config/api';

async function readError(res) {
  try {
    const body = await res.json();
    return body.message || body.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export default function useWorkshopChain(instanceId) {
  const [chain, setChain] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!instanceId) return;
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCE_CHAIN(instanceId));
      if (!res.ok) throw new Error(await readError(res));
      setChain(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(API_ENDPOINTS.WORKSHOP_CATALOG());
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        if (!cancelled) setCatalog(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [instanceId, reload]);

  /** Per-module run settings; saved at once, then the chain is re-read. */
  const patchStep = useCallback(async (stepId, patch) => {
    const res = await fetch(API_ENDPOINTS.INSTANCE_CHAIN_STEP(instanceId, stepId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(await readError(res));
    await reload();
    return res.json();
  }, [instanceId, reload]);

  const lookups = useMemo(() => {
    const index = (list, key) => Object.fromEntries((list || []).map((x) => [x[key], x]));
    return {
      modules: index(catalog?.modules, 'id'),
      handoffs: index(catalog?.handoffs, 'id'),
      manualInputs: index(catalog?.manualInputs, 'id'),
    };
  }, [catalog]);

  return { chain, catalog, lookups, error, loading, reload, patchStep };
}
